import * as THREE from 'three'
import { CentersPointsMaterial } from '@/plugins/gsplat/materials/CentersPointsMaterial'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'
import type { UnifiedLODMesh } from './UnifiedLODMesh'

/**
 * 中心点渲染 Points Pass
 *
 * 独立的 THREE.Points 对象，用于渲染 splat 中心点。
 * 不独立做 LOD 更新，通过 gaussianMesh.updateFrameState() 获取同一帧的共享结果。
 *
 * 使用方式：
 * - gaussian-only: centersPoints.visible = false
 * - centers-only: centersPoints.visible = true, gaussianMesh.visible = false
 * - gaussian-with-centers: 两者都可见，centersPoints.renderOrder > gaussianMesh.renderOrder
 */
export class UnifiedLODCentersPoints extends THREE.Points<
  THREE.BufferGeometry,
  CentersPointsMaterial
> {
  /** 关联的高斯渲染 Mesh（帧状态来源） */
  private gaussianMesh: UnifiedLODMesh

  /** 当前 dummy buffer 大小（避免频繁重分配） */
  private _dummyBufferSize = 0

  /**
   * 创建中心点渲染 Points
   *
   * @param gaussianMesh - 关联的 UnifiedLODMesh（帧状态来源）
   */
  constructor(gaussianMesh: UnifiedLODMesh) {
    // 创建空的 BufferGeometry（使用 gl_VertexID 在 shader 中索引数据）
    const geometry = new THREE.BufferGeometry()
    // 需要一个 dummy position attribute 让 Three.js 知道可绘制顶点范围
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3))
    geometry.boundingBox = new THREE.Box3()
    geometry.boundingSphere = new THREE.Sphere()

    const material = new CentersPointsMaterial()

    super(geometry, material)

    this.gaussianMesh = gaussianMesh

    // renderOrder 要高于高斯 mesh（高斯 mesh 默认 1000）
    this.renderOrder = 1001

    // 禁用视锥剔除（与高斯 mesh 一致）
    this.frustumCulled = false

    // 默认不可见（gaussian-only 模式）
    this.visible = false
  }

  /**
   * 渲染前回调（Three.js 自动调用）
   *
   * 通过 gaussianMesh.updateFrameState() 获取共享帧状态，
   * 绑定中心点 pass 所需的 uniforms。
   */
  onBeforeRender = (
    renderer: THREE.WebGLRenderer,
    _scene: THREE.Scene,
    camera: THREE.Camera
  ): void => {
    // 从高斯 mesh 获取共享帧状态（需要状态纹理）
    const state = this.gaussianMesh.updateFrameState(renderer, camera, true)
    if (!state) {
      // 设置 drawRange 为 0 避免绘制
      this.geometry.setDrawRange(0, 0)
      return
    }

    const manager = state.manager!
    if (manager.isWorkBufferRenderable === false) {
      this.geometry.setDrawRange(0, 0)
      return
    }

    // 更新顶点数（每个 splat 一个顶点）
    this.geometry.setDrawRange(0, state.visibleSplatCount)
    // 确保 dummy position buffer 足够大（只增不减，避免频繁分配）
    if (state.visibleSplatCount > this._dummyBufferSize) {
      // 按 2 倍增长策略分配
      const newSize = Math.max(state.visibleSplatCount, this._dummyBufferSize * 2)
      this.geometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(newSize * 3), 3)
      )
      this._dummyBufferSize = newSize
    }

    // 同步包围盒/包围球（避免 Three.js 剔除）
    if (this.gaussianMesh.geometry.boundingBox) {
      this.geometry.boundingBox!.copy(this.gaussianMesh.geometry.boundingBox)
    }
    if (this.gaussianMesh.geometry.boundingSphere) {
      this.geometry.boundingSphere!.copy(this.gaussianMesh.geometry.boundingSphere)
    }

    // 绑定 WorkBuffer 数据到材质
    this.material.setWorkBufferData(
      manager.splatTexture0,
      manager.orderTexture,
      state.visibleSplatCount,
      state.textureSize
    )

    // 绑定状态纹理
    this.material.setSplatStateTexture(state.mergedStateTexture)

    // 设置视口
    this.material.setViewport(state.viewportWidth, state.viewportHeight)

    // 设置相机参数
    this.material.setCameraParams(camera)

    // 绑定标量过滤
    const filterConfig = manager.getScalarFilterConfig()
    if (filterConfig?.enabled && manager.scalarTexture) {
      this.material.setScalarTexture(manager.scalarTexture)
      this.material.setScalarFilter(true, filterConfig.min, filterConfig.max)
    } else {
      this.material.setScalarTexture(null)
      this.material.setScalarFilter(false, 0, 1)
    }

    // 与 gaussian pass 共享同一裁切 packet，centers-only 时也主动同步资产矩阵。
    this.material.setClipPacket(this.gaussianMesh.getCurrentClipPacket())
  }

  /**
   * 销毁
   */
  dispose(): void {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this.geometry.dispose()
    this.material.dispose()
  }
}
